import type { ArgumentsType, DataSourceAbstract, EndArg } from '@lighthouse/core'
import type { Workflow } from '@lighthouse/shared'
import { ANONYMOUS_PARAM_NAME, type FlowNode, getVirtualFieldByInnerType, placeholderFromInnerType } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import React, { useCallback } from 'react'

import { AddArgButton } from './AddArgButton'
import { ArgItem } from './ArgItem'

interface SettingOutputArgProps {
    value: EndArg[] | undefined
    onChange: (value: EndArg[]) => void
    allParentNodes?: FlowNode[]
    upstreamNodes?: FlowNode[]
    dataSourceList: DataSourceAbstract[]
    workflowList: Workflow[]
}

export const SettingOutputArg: React.FC<SettingOutputArgProps> = ({
    value,
    allParentNodes,
    upstreamNodes,
    dataSourceList,
    workflowList,
    onChange
}) => {
    const handleItemClick = useCallback(
        (type: ArgumentsType) => {
            const id = nanoid(12)
            const item: EndArg = {
                id,
                name: ANONYMOUS_PARAM_NAME,
                innerType: type,
                value: undefined
            }
            const newList = value ? [...value, item] : [item]
            onChange(newList)
        },
        [value, onChange]
    )

    const handleItemDelete = useCallback(
        (index: number) => {
            const newList = value ? [...value] : []
            newList.splice(index, 1)
            onChange(newList)
        },
        [value, onChange]
    )

    const handleItemChange = useCallback(
        (index: number, newValue: EndArg) => {
            const newList = value ? [...value] : []
            const itemValue = value?.[index]
            if (itemValue) {
                newList.splice(index, 1, newValue)
                onChange(newList)
            }
        },
        [value, onChange]
    )
    // SYSTEM_FIELD
    return (
        <>
            {value?.map((item, index) => {
                const nodeVariable = item
                const placeholder = placeholderFromInnerType[nodeVariable.innerType]
                const virtualField = getVirtualFieldByInnerType(nodeVariable.innerType)
                if (!virtualField) {
                    return null
                }
                return (
                    <ArgItem
                        autoHeight
                        dataSourceList={dataSourceList}
                        workflowList={workflowList}
                        key={nodeVariable.id}
                        placeholder={placeholder}
                        allParentNodes={allParentNodes}
                        upstreamNodes={upstreamNodes}
                        field={virtualField}
                        data={nodeVariable}
                        onItemDelete={() => handleItemDelete(index)}
                        onChange={val => handleItemChange(index, val)}
                    />
                )
            })}
            <AddArgButton onItemClick={handleItemClick} />
        </>
    )
}
